import React, { Suspense, useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HomePage from './components/HomePage.tsx';
import SubCategoryPage from './components/SubCategoryPage.tsx';
import ItemDetails from './components/ItemDetails.tsx';
import PWAPrompt from './components/PWAPrompt.tsx';
import NotificationPrompt from './components/NotificationPrompt.tsx';
import CartPage from './components/CartPage.tsx';
import CartIcon from './components/CartIcon.tsx';
import NotFound from './components/NotFound.tsx';
import SendNotification from './components/SendNotification.tsx';
import LoadingScreen from './components/LoadingScreen.tsx';
import LoginModal from './components/auth/LoginModal.tsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { createGlobalStyle } from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { initializeOneSignal } from './services/oneSignal.ts';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { CartProvider } from './contexts/CartContext.tsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { FollowProvider } from './contexts/FollowContext.tsx';
import { 
  initializeGoogleSheetsService, 
  cleanupLocationServices,
  checkInitialDataAvailable,
  updateSheetId,
  determineSheetIdByLocation,
  createGoogleMapsLink,
  DEFAULT_SHEET_ID,
  fetchZoneMappings
} from './services/googleSheets.ts';
import { ThemeProvider } from 'styled-components';
import ZoneSettings from './components/ZoneSettings.tsx';
import styled from 'styled-components';

const GlobalStyle = createGlobalStyle`
  :root {
    --primary-gradient: linear-gradient(135deg, #00BFA6, #0288D1);
    --secondary-gradient: linear-gradient(135deg, #FF6B6B, #FF9F43);
    --glass-bg: rgba(255, 255, 255, 0.85);
    --glass-border: rgba(255, 255, 255, 0.25);
    --box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
    --text-primary: #2c3e50;
    --text-secondary: #666;
    --border-radius: 20px;
    --card-hover-y: -8px;
  }

  body {
    background: linear-gradient(135deg, #f6f9fc 0%, #ecf0f3 100%);
    background-attachment: fixed;
    min-height: 100vh;
    font-family: 'Tajawal', sans-serif;
    direction: rtl;
    color: var(--text-primary);
  }

  .glass-card {
    background: var(--glass-bg);
    backdrop-filter: blur(10px);
    border: 1px solid var(--glass-border);
    border-radius: var(--border-radius);
    box-shadow: var(--box-shadow);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .glass-card:hover {
    transform: translateY(var(--card-hover-y));
    box-shadow: 0 12px 40px 0 rgba(31, 38, 135, 0.25);
  }

  .section-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 2rem;
    background: var(--primary-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-align: center;
    position: relative;
    padding-bottom: 1rem;
  }

  .section-title::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: var(--primary-gradient);
    border-radius: 2px;
  }

  .modern-button {
    background: var(--primary-gradient);
    color: white;
    padding: 12px 24px;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 15px rgba(0, 191, 166, 0.2);
  }

  .modern-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 191, 166, 0.3);
  }

  .page-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 2rem;
  }

  /* Modern Scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #00BFA6, #0288D1);
    border-radius: 4px;
  }

  /* Modern Form Elements */
  input, select, textarea {
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: 12px;
    padding: 12px 16px;
    transition: all 0.3s ease;
  }

  input:focus, select:focus, textarea:focus {
    outline: none;
    border-color: #00BFA6;
    box-shadow: 0 0 0 3px rgba(0, 191, 166, 0.1);
  }

  /* Loading Animation */
  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 50vh;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 3px solid rgba(0, 191, 166, 0.1);
    border-top: 3px solid #00BFA6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Grid Layouts */
  .modern-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 2rem;
    padding: 2rem 0;
  }

  /* Card Animations */
  .card-hover-effect {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .card-hover-effect:hover {
    transform: translateY(var(--card-hover-y));
  }

  /* Image Containers */
  .image-container {
    position: relative;
    overflow: hidden;
    border-radius: var(--border-radius);
  }

  .image-container img {
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .image-container:hover img {
    transform: scale(1.1);
  }

  /* Text Gradients */
  .gradient-text {
    background: var(--primary-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .section-title {
      font-size: 2rem;
    }

    .modern-grid {
      grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    }
  }

  /* تنسيقات شاشة طلب تسجيل الدخول */
  .auth-required-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 2rem;
    text-align: center;
    background: linear-gradient(135deg, #f6f9fc 0%, #ecf0f3 100%);
  }

  .auth-required-message {
    background: var(--glass-bg);
    backdrop-filter: blur(10px);
    border: 1px solid var(--glass-border);
    border-radius: var(--border-radius);
    box-shadow: var(--box-shadow);
    padding: 3rem;
    max-width: 600px;
    margin: 0 auto;
    text-align: center;
  }

  .auth-required-message h2 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: var(--text-primary);
  }

  .auth-required-message p {
    font-size: 1.2rem;
    color: var(--text-secondary);
    margin-bottom: 2rem;
  }

  .zone-settings-button {
    position: fixed;
    bottom: 20px;
    left: 20px;
    background: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 1000;
    
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    }
    
    svg {
      width: 24px;
      height: 24px;
      color: #00BFA6;
    }
    
    span {
      display: none;
    }
    
    @media (min-width: 768px) {
      width: auto;
      height: auto;
      padding: 10px 20px;
      border-radius: 25px;
      
      span {
        display: inline;
        margin-right: 8px;
        color: #333;
        font-weight: 500;
      }
    }
  }
`;

// Add theme definition
const theme = {
  colors: {
    primary: '#00bfa6',
    secondary: '#f0f2f5',
    text: '#333333',
    lightText: '#666666',
  }
};

// مكون التوجيه المحمي
interface ProtectedRouteProps {
  children: React.ReactNode;
  skipLocationCheck?: boolean; // إضافة خاصية لتخطي التحقق من الموقع
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, skipLocationCheck = false }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(!isAuthenticated);

  // عند إغلاق المودال وعدم تسجيل الدخول، قم بإعادة توجيه المستخدم إلى الصفحة الرئيسية
  const handleLoginModalClose = () => {
    setIsLoginModalOpen(false);
    // إذا ظل المستخدم غير مسجل الدخول وأغلق النافذة، يمكن إضافة منطق إضافي هنا
  };

  // إذا لم يكن المستخدم مسجل، اعرض مودال تسجيل الدخول
  if (!isAuthenticated) {
    return (
      <div className="auth-required-container">
        <div className="auth-required-message">
          <h2>يرجى تسجيل الدخول للاستمرار</h2>
          <p>يجب عليك تسجيل الدخول أو إنشاء حساب جديد للوصول إلى هذه الصفحة</p>
          <button 
            className="modern-button"
            onClick={() => setIsLoginModalOpen(true)}
          >
            تسجيل الدخول
          </button>
        </div>
        <LoginModal 
          isOpen={isLoginModalOpen} 
          onClose={handleLoginModalClose} 
        />
      </div>
    );
  }

  // إذا كان المستخدم مسجل، اعرض المحتوى المطلوب
  return <>{children}</>;
};

// مكون للصفحات التي لا تتطلب تحديد الموقع
const LocationFreeRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // يمكن هنا إضافة أي منطق خاص بالصفحات التي لا تتطلب تحديد الموقع
  return <>{children}</>;
};

const ZoneSettingsButton = styled.button`
  position: fixed;
  top: 20px;
  left: 20px;
  background: white;
  border: 2px solid #00BFA6;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: #00BFA6;
  }
  
  @media (min-width: 768px) {
    top: 30px;
    left: 30px;
    width: 45px;
    height: 45px;
  }
`;

// إضافة متغيرات عامة
let locationTimeout: NodeJS.Timeout | null = null;
let isLocationRequested = false;

const App = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('جاري تحميل التطبيق...');
  const [currentZone, setCurrentZone] = useState<string>(() => {
    return localStorage.getItem('selectedZone') || '';
  });
  const [currentGovernorate, setCurrentGovernorate] = useState<string>(() => {
    return localStorage.getItem('selectedGovernorate') || '';
  });
  const [currentSheetId, setCurrentSheetId] = useState<string>(() => {
    return localStorage.getItem('selectedSheetId') || '';
  });
  const [isAutomatic, setIsAutomatic] = useState<boolean>(() => {
    const savedMode = localStorage.getItem('isAutomatic');
    return savedMode !== null ? savedMode === 'true' : true;
  });
  const [locationDetermined, setLocationDetermined] = useState<boolean>(() => {
    return localStorage.getItem('locationDetermined') === 'true';
  });
  
  const [showZoneSettings, setShowZoneSettings] = useState(false);
  const [shouldSkipLocationCheck, setShouldSkipLocationCheck] = useState(false);
  const [locationStatus, setLocationStatus] = useState<{
    isLocationSupported?: boolean;
    isLocationPermitted?: boolean;
    accuracy?: number;
    usingDefault?: boolean;
    mapsLink?: string;
    latitude?: number;
    longitude?: number;
  }>({});

  // Función para obtener el estado actual del sheet
  const getCurrentSheetStatus = () => {
    return {
      sheetId: currentSheetId || localStorage.getItem('selectedSheetId') || DEFAULT_SHEET_ID,
      zone: currentZone || localStorage.getItem('selectedZone') || '',
      governorate: currentGovernorate || localStorage.getItem('selectedGovernorate') || '',
      isAutomatic: isAutomatic || localStorage.getItem('isAutomatic') === 'true'
    };
  };

  const reloadData = async (forcedSheetId?: string) => {
    try {
      // بدء تحميل البيانات
      setLoading(true);
      
      // استخدام SheetId المحدد أو من الحالة أو من التخزين المحلي أو الافتراضي
      const sheetIdToUse = forcedSheetId || currentSheetId || localStorage.getItem('selectedSheetId') || DEFAULT_SHEET_ID;
      // console.log('Reloading data for sheet ID:', sheetIdToUse);
      
      // تحديث SheetId في الخدمة
      updateSheetId(sheetIdToUse);
      
      // فحص إذا كانت البيانات متاحة
      // console.log('Checking data availability...');
      const dataAvailable = await checkInitialDataAvailable();
      
      if (dataAvailable) {
        // console.log('Data available for SheetId:', sheetIdToUse);
        // تحديث الحالة لعرض البيانات
        setIsDataLoaded(true);
        // مسح أي أخطاء سابقة
        setError(null);
        // عرض رسالة نجاح
        toast.success('تم تحميل البيانات بنجاح', { autoClose: 1000 });
        
        // نشر حدث تحديث البيانات
        window.dispatchEvent(new CustomEvent('dataUpdated', {
          detail: { sheetId: sheetIdToUse }
        }));
        
        return true;
      } else {
        console.error('Data not available for SheetId:', sheetIdToUse);
        
        // إذا كان هناك sheetId محدد ولم يعمل، نحاول استخدام الشيت الافتراضي
        if (forcedSheetId && forcedSheetId !== DEFAULT_SHEET_ID) {
          // console.log('Trying with default sheet ID instead');
          setCurrentSheetId(DEFAULT_SHEET_ID);
          updateSheetId(DEFAULT_SHEET_ID);
          const defaultDataAvailable = await checkInitialDataAvailable();
          
          if (defaultDataAvailable) {
            setIsDataLoaded(true);
            setError('تعذر تحميل البيانات للمنطقة المحددة، تم استخدام البيانات الافتراضية');
            toast.warning('تعذر تحميل البيانات للمنطقة المحددة، تم استخدام البيانات الافتراضية', { autoClose: 2000 });
            
            // نشر حدث تحديث البيانات للشيت الافتراضي
            window.dispatchEvent(new CustomEvent('dataUpdated', {
              detail: { sheetId: DEFAULT_SHEET_ID }
            }));
            
            return true;
          }
        }
        
        // استخدام الشيت الافتراضي دائمًا كخطة بديلة نهائية
        // console.log('Falling back to default sheet ID');
        setCurrentSheetId(DEFAULT_SHEET_ID);
        updateSheetId(DEFAULT_SHEET_ID);
        setIsDataLoaded(true);
        
        // إظهار رسالة خطأ
        setError('تعذر تحميل البيانات للمنطقة المحددة، تم استخدام البيانات الافتراضية');
        toast.error('تعذر تحميل البيانات للمنطقة المحددة', { autoClose: 2000 });
        
        return false;
      }
    } catch (error) {
      // التعامل مع الأخطاء
      console.error('Error in reloadData:', error);
      
      // استخدام الشيت الافتراضي
      setCurrentSheetId(DEFAULT_SHEET_ID);
      updateSheetId(DEFAULT_SHEET_ID);
      setIsDataLoaded(true);
      
      // إظهار رسالة خطأ
      setError('حدث خطأ أثناء تحميل البيانات، تم استخدام البيانات الافتراضية');
      toast.error('حدث خطأ أثناء تحميل البيانات', { autoClose: 2000 });
      
      return false;
    } finally {
      // إنهاء حالة التحميل
      setLoading(false);
      setIsInitializing(false);
    }
  };

  const initializeLocation = async () => {
    try {
      // منع طلبات متعددة في نفس الوقت
      if (isLocationRequested) {
        // console.log('Location request already in progress, skipping');
        return;
      }
      
      isLocationRequested = true;
      // console.log('Starting location initialization');
      setIsInitializing(true);
      
      // التحقق من البيانات المحفوظة
      const savedSheetId = localStorage.getItem('selectedSheetId');
      const savedZone = localStorage.getItem('selectedZone');
      const savedGovernorate = localStorage.getItem('selectedGovernorate');
      const savedIsAutomatic = localStorage.getItem('isAutomatic');
      const locationDetermined = localStorage.getItem('locationDetermined') === 'true';
      
      // console.log('Saved data:', {
      //   savedSheetId,
      //   savedZone,
      //   savedGovernorate,
      //   savedIsAutomatic,
      //   locationDetermined
      // });

      // استخدام البيانات المحفوظة إذا وجدت
      if (savedSheetId && savedZone) {
        // console.log('Using saved data');
        setCurrentSheetId(savedSheetId);
        setCurrentZone(savedZone);
        setCurrentGovernorate(savedGovernorate || '');
        setIsAutomatic(savedIsAutomatic === 'true');
        updateSheetId(savedSheetId);
        setIsDataLoaded(true);
        setIsInitializing(false);
        isLocationRequested = false;
        return;
      }

      // إذا كان الوضع يدوي، استخدم الشيت الافتراضي
      if (savedIsAutomatic === 'false') {
        // console.log('Manual mode - using default sheet');
        setCurrentSheetId(DEFAULT_SHEET_ID);
        updateSheetId(DEFAULT_SHEET_ID);
        setIsDataLoaded(true);
        setIsInitializing(false);
        isLocationRequested = false;
        return;
      }

      // بدء تحميل البيانات الافتراضية بعد فترة قصيرة
      locationTimeout = setTimeout(() => {
        // console.log('Location taking too long, loading default data');
        setCurrentSheetId(DEFAULT_SHEET_ID);
        updateSheetId(DEFAULT_SHEET_ID);
        setIsDataLoaded(true);
        setIsInitializing(false);
      }, 2000);

      // محاولة تحديد الموقع
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            // تم تحديد الموقع بنجاح
            if (locationTimeout) {
              clearTimeout(locationTimeout);
              locationTimeout = null;
            }
            
            try {
              const newSheetId = await determineSheetIdByLocation(
                position.coords.latitude,
                position.coords.longitude
              );

              if (newSheetId) {
                const zoneMappings = await fetchZoneMappings();
                const matchingZone = zoneMappings.find(zone => zone.sheetUrl.includes(newSheetId));
                
                if (matchingZone) {
                  setCurrentZone(matchingZone.zone);
                  setCurrentGovernorate(matchingZone.governorate || '');
                  localStorage.setItem('selectedZone', matchingZone.zone);
                  localStorage.setItem('selectedGovernorate', matchingZone.governorate || '');
                }

                localStorage.setItem('selectedSheetId', newSheetId);
                localStorage.setItem('locationDetermined', 'true');
                
                setCurrentSheetId(newSheetId);
                updateSheetId(newSheetId);
                setIsDataLoaded(true);
              } else {
                // console.log('No matching zone found, using default');
                setCurrentSheetId(DEFAULT_SHEET_ID);
                updateSheetId(DEFAULT_SHEET_ID);
              }
            } catch (error) {
              console.error('Error processing location:', error);
              setCurrentSheetId(DEFAULT_SHEET_ID);
              updateSheetId(DEFAULT_SHEET_ID);
            }
            setIsInitializing(false);
          },
          (error) => {
            // خطأ في تحديد الموقع
            if (locationTimeout) {
              clearTimeout(locationTimeout);
              locationTimeout = null;
            }
            // console.log('Location error:', error);
            
            setCurrentSheetId(DEFAULT_SHEET_ID);
            updateSheetId(DEFAULT_SHEET_ID);
            setIsDataLoaded(true);
            
            if (error.code === 1) { // رفض الإذن
              setLocationError('تم رفض إذن تحديد الموقع');
              setLocationStatus(prev => ({ ...prev, isLocationPermitted: false }));
              // تعيين الوضع اليدوي عند رفض الإذن
              setIsAutomatic(false);
              localStorage.setItem('isAutomatic', 'false');
            }
            
            setIsInitializing(false);
          },
          {
            enableHighAccuracy: false,
            timeout: 5000,
            maximumAge: 300000
          }
        );
      } else {
        // الموقع غير مدعوم
        if (locationTimeout) {
          clearTimeout(locationTimeout);
          locationTimeout = null;
        }
        // console.log('Geolocation not supported');
        
        setCurrentSheetId(DEFAULT_SHEET_ID);
        updateSheetId(DEFAULT_SHEET_ID);
        setIsDataLoaded(true);
        setIsInitializing(false);
      }
    } catch (error) {
      // خطأ عام
      if (locationTimeout) {
        clearTimeout(locationTimeout);
        locationTimeout = null;
      }
      console.error('Error in initializeLocation:', error);
      
      setLocationError('حدث خطأ أثناء تهيئة الموقع');
      setCurrentSheetId(DEFAULT_SHEET_ID);
      updateSheetId(DEFAULT_SHEET_ID);
      setIsDataLoaded(true);
      setIsInitializing(false);
    } finally {
      isLocationRequested = false;
    }
  };
  
  useEffect(() => {
    // تهيئة OneSignal
    initializeOneSignal();
    
    // تهيئة الموقع مرة واحدة فقط
    if (!isInitialized) {
      // console.log('First time initialization');
      initializeLocation();
      setIsInitialized(true);
    }
    
    return () => {
      // تنظيف المؤقتات عند إزالة المكون
      if (locationTimeout) {
        clearTimeout(locationTimeout);
        locationTimeout = null;
      }
      
      // تنظيف خدمات الموقع
      if (isAutomatic) {
        cleanupLocationServices();
      }
    };
  }, [isInitialized]);

  // Determine if we should continue showing the loading screen
  const showLoading = !shouldSkipLocationCheck && (isInitializing || (!isDataLoaded && !locationStatus.usingDefault));
  
  // Customize loading message based on state
  let loadingSubMessage = isAutomatic 
    ? "نحن نحدد موقعك لعرض البيانات المناسبة لمنطقتك" 
    : "جاري تحميل البيانات للمنطقة المختارة";
  
  if (isInitialized && !isDataLoaded) {
    loadingSubMessage = "جاري تحميل البيانات...";
  }

  const handleRetry = () => {
    // Reset states
    setIsInitializing(true);
    setIsInitialized(false);
    setIsDataLoaded(false);
    setLocationError(null);
    
    // Try again
    initializeLocation();
  };

  const handleZoneSelect = async (zone: string, sheetId: string, governorate: string) => {
    try {
      // بدء عملية التغيير
      setIsInitializing(true);
      setLoadingMessage(`جاري تحميل البيانات لمنطقة ${zone}...`);
      // console.log(`Selecting zone: ${zone}, sheetId: ${sheetId}`);
      
      // تحديث الحالة أولاً
      setCurrentZone(zone);
      setCurrentSheetId(sheetId);
      setCurrentGovernorate(governorate);
      setIsAutomatic(false);
      
      // حفظ البيانات في localStorage
      localStorage.setItem('selectedZone', zone);
      localStorage.setItem('selectedSheetId', sheetId);
      localStorage.setItem('selectedGovernorate', governorate);
      localStorage.setItem('isAutomatic', 'false');
      localStorage.setItem('locationDetermined', 'true');
      // console.log('Zone data saved to localStorage');
      
      // تحديث SheetId مباشرة قبل تحميل البيانات
      // console.log(`Updating sheet ID to: ${sheetId}`);
      updateSheetId(sheetId);
      
      // تحميل البيانات الجديدة مع تمرير sheetId مباشرة
      // console.log('Loading data for selected zone...');
      await reloadData(sheetId);
      
      // نشر حدث تغيير المنطقة لتحديث المكونات
      window.dispatchEvent(new CustomEvent('zoneUpdated', {
        detail: { zone, sheetId, governorate }
      }));
      
      // إغلاق مودال الإعدادات ​​وعرض رسالة نجاح
      setShowZoneSettings(false);
      toast.success(`تم تحديث المنطقة إلى ${zone}`, { autoClose: 2000 });
    } catch (error) {
      console.error('Error in handleZoneSelect:', error);
      toast.error('حدث خطأ أثناء تحديث المنطقة', { autoClose: 2000 });
      
      // إعادة الحالة إلى ما كانت عليه
      const savedSheetId = localStorage.getItem('selectedSheetId');
      const savedZone = localStorage.getItem('selectedZone');
      const savedGovernorate = localStorage.getItem('selectedGovernorate');
      
      if (savedSheetId && savedZone) {
        setCurrentSheetId(savedSheetId);
        setCurrentZone(savedZone);
        setCurrentGovernorate(savedGovernorate || '');
        updateSheetId(savedSheetId);
      } else {
        setCurrentSheetId(DEFAULT_SHEET_ID);
        updateSheetId(DEFAULT_SHEET_ID);
      }
    } finally {
      setIsInitializing(false);
    }
  };

  const handleAutomaticChange = async (isAuto: boolean) => {
    try {
      // بدء عملية التغيير
      setIsInitializing(true);
      setLoadingMessage(isAuto ? 'جاري تحديد موقعك...' : 'جاري التغيير للوضع اليدوي...');
      // console.log(`Changing mode to: ${isAuto ? 'automatic' : 'manual'}`);
      
      // تحديث الحالة
      setIsAutomatic(isAuto);
      localStorage.setItem('isAutomatic', isAuto.toString());
      
      if (isAuto) {
        // الوضع التلقائي - تحديد الموقع
        // console.log('Switching to automatic mode, determining location...');
        
        // إعادة تهيئة تحديد الموقع
        localStorage.removeItem('locationDetermined');
        setLocationDetermined(false);
        
        // بدء تحديد الموقع وتحميل البيانات
        await initializeLocation();
        
        // إظهار رسالة نجاح
        toast.success('تم التغيير إلى الوضع التلقائي وتحديد موقعك', { autoClose: 2000 });
        
        // إغلاق المودال
        setShowZoneSettings(false);
      } else {
        // الوضع اليدوي - عرض إعدادات المنطقة
        // console.log('Switching to manual mode, showing zone settings...');
        
        // عرض مودال اختيار المنطقة
        setShowZoneSettings(true);
        
        // إظهار رسالة
        toast.info('تم التغيير إلى الوضع اليدوي، يرجى اختيار منطقتك', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Error in handleAutomaticChange:', error);
      toast.error('حدث خطأ أثناء تغيير الوضع', { autoClose: 2000 });
    } finally {
      setIsInitializing(false);
    }
  };

  // Efecto para inicializar la ubicación solo una vez al cargar
  useEffect(() => {
    if (!isInitialized) {
      initializeLocation();
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Añadir un nuevo useEffect después del useEffect principal que ya existe
  useEffect(() => {
    // Manejar actualización de zona
    const handleZoneUpdated = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      // console.log('Zona actualizada:', detail);
      
      // Actualizar datos basados en la nueva zona
      checkInitialDataAvailable().then(available => {
        if (available) {
          // console.log('Datos disponibles para la nueva zona');
          setIsDataLoaded(true);
          
          // Disparar evento para que los componentes se actualicen
          window.dispatchEvent(new CustomEvent('dataUpdated'));
        } else {
          console.error('No se pudieron cargar datos para la nueva zona');
          setError('لا يمكن تحميل البيانات للمنطقة المحددة');
        }
      });
    };
    
    // Manejar cambio de modo (automático/manual)
    const handleModeUpdated = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      // console.log('Modo actualizado:', detail);
      
      // Si cambiamos a modo automático, verificar que los datos estén disponibles
      if (detail?.isAutomatic) {
        checkInitialDataAvailable().then(available => {
          if (available) {
            // console.log('Datos disponibles después de cambio a modo automático');
            setIsDataLoaded(true);
            
            // Disparar evento para actualizar componentes
            window.dispatchEvent(new CustomEvent('dataUpdated'));
          } else {
            console.error('No se pudieron cargar datos en modo automático');
            setError('لا يمكن تحميل البيانات في الوضع التلقائي');
          }
        });
      }
    };
    
    // Escuchar eventos personalizados
    window.addEventListener('zoneUpdated', handleZoneUpdated);
    window.addEventListener('modeUpdated', handleModeUpdated);
    
    // Limpiar listeners al desmontar
    return () => {
      window.removeEventListener('zoneUpdated', handleZoneUpdated);
      window.removeEventListener('modeUpdated', handleModeUpdated);
    };
  }, []);

  // Añadir también un useEffect para manejar dataUpdated en componentes hijos
  useEffect(() => {
    const handleDataUpdated = () => {
      // console.log('Datos actualizados, refrescando componentes');
      // Forzar re-renderizado de componentes sin recargar la página
      setIsDataLoaded(prev => {
        // Toggle para forzar re-renderizado
        setTimeout(() => setIsDataLoaded(true), 0);
        return false;
      });
    };
    
    window.addEventListener('dataUpdated', handleDataUpdated);
    return () => {
      window.removeEventListener('dataUpdated', handleDataUpdated);
    };
  }, []);

  // التأثير الرئيسي لتهيئة التطبيق
  useEffect(() => {
    // تهيئة البيانات والموقع الأولي
    if (!isInitialized) {
      // console.log('Initializing app...');
      setIsInitialized(true);
      
      // تهيئة OneSignal بالدالة الصحيحة
      initializeOneSignal();
      
      // تهيئة الموقع مرة واحدة عند بدء التشغيل
      initializeLocation();
    }
    
    // إضافة مستمعات للأحداث لتحديث المكونات
    const handleZoneUpdate = (event: any) => {
      const { zone, sheetId, governorate } = event.detail;
      // console.log(`Zone updated event received: ${zone}, ${sheetId}`);
      
      // لا داعي لاستدعاء reloadData هنا لأنه تم استدعاءه بالفعل في handleZoneSelect
      // تحديث حالة المكونات فقط
      setCurrentZone(zone);
      setCurrentSheetId(sheetId);
      setCurrentGovernorate(governorate);
    };
    
    const handleDataUpdate = (event: any) => {
      const { sheetId } = event.detail;
      // console.log(`Data updated event received for sheetId: ${sheetId}`);
      
      // تحديث الحالة لتعكس تحميل البيانات الجديدة
      setIsDataLoaded(true);
      setLoading(false);
    };
    
    // تسجيل مستمعات الأحداث
    window.addEventListener('zoneUpdated', handleZoneUpdate);
    window.addEventListener('dataUpdated', handleDataUpdate);
    
    // تنظيف المستمعات عند إزالة المكون
    return () => {
      window.removeEventListener('zoneUpdated', handleZoneUpdate);
      window.removeEventListener('dataUpdated', handleDataUpdate);
      
      // تنظيف المؤقتات لمنع تسرب الذاكرة
      if (locationTimeout) {
        clearTimeout(locationTimeout);
      }
    };
  }, [isInitialized]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      {showLoading ? (
        <LoadingScreen 
          message={loadingMessage} 
          subMessage={loadingSubMessage} 
          error={locationError || undefined}
          onRetry={locationError ? handleRetry : undefined}
          locationStatus={locationStatus}
        />
      ) : (
    <HelmetProvider>
      <AuthProvider>
        <FollowProvider>
              <CartProvider>
          <Router>
              <div className="App">
                <PWAPrompt />
                <NotificationPrompt />
                <CartIcon />
                    <ZoneSettingsButton onClick={() => setShowZoneSettings(true)}>
                      <svg viewBox="0 0 24 24">
                        <path d="M12 8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6-3h-1V4c0-1.1-.9-2-2-2H9c-1.1 0-2 .9-2 2v1H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zM9 4h6v1H9V4zm9 15H6V7h12v12z"/>
                      </svg>
                    </ZoneSettingsButton>
                <Suspense fallback={<div>جاري التحميل...</div>}>
                  <Routes>
                        {/* جميع المسارات تستخدم التوجيه المحمي */}
                        <Route path="/" element={
                          <ProtectedRoute>
                            <HomePage />
                          </ProtectedRoute>
                        } />
                        <Route path="/:url" element={
                          <ProtectedRoute>
                            <SubCategoryPage />
                          </ProtectedRoute>
                        } />
                        <Route path="/:subcategory/:url" element={
                          <ProtectedRoute>
                            <ItemDetails />
                          </ProtectedRoute>
                        } />
                        <Route path="/cart" element={
                          <ProtectedRoute>
                            <CartPage />
                          </ProtectedRoute>
                        } />
                        {/* صفحة إرسال الإشعارات لا تتطلب تحديد الموقع */}
                        <Route path="/send-notification" element={
                          <ProtectedRoute skipLocationCheck={true}>
                            <LocationFreeRoute>
                              <SendNotification />
                            </LocationFreeRoute>
                          </ProtectedRoute>
                        } />
                        <Route path="*" element={
                          <ProtectedRoute>
                            <NotFound />
                          </ProtectedRoute>
                        } />
                        <Route path="/login" element={<LoginModal isOpen={true} onClose={() => {}} />} />
                  </Routes>
                </Suspense>
                <ToastContainer
                  position="top-center"
                  autoClose={500}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={true}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="light"
                />
                    <ZoneSettings
                      isOpen={showZoneSettings}
                      onClose={() => setShowZoneSettings(false)}
                      onZoneSelect={handleZoneSelect}
                      currentZone={currentZone}
                      currentGovernorate={currentGovernorate}
                      isAutomatic={isAutomatic}
                      onAutomaticChange={handleAutomaticChange}
                />
              </div>
                </Router>
            </CartProvider>
        </FollowProvider>
      </AuthProvider>
    </HelmetProvider>
      )}
    </ThemeProvider>
  );
};

export default App;
